import { Component, type ErrorInfo, type ReactNode } from 'react';

type Props = {
  children: ReactNode;
};

type State = {
  error: Error | null;
  info: ErrorInfo | null;
};

/**
 * 全局错误兜底 · 避免任何组件渲染抛异常导致整页白屏
 * - 生产：只显示友好信息 + "重新加载" / "回首页" 两个动作
 * - 开发（import.meta.env.DEV）：额外展开 error stack + component stack
 *
 * ⚠️ React 错误边界的固有限制（非本组件 bug）：以下情形捕获不到，由各处单独处理：
 *   - 事件处理器里的异步错误 → 在 onClick 等回调里 try/catch 或用 mutation onError
 *   - useEffect 异步错误 / unhandled Promise rejection → React Query 的 isError 已覆盖
 *     大部分；剩余可在 main.tsx 注册 window.onunhandledrejection
 *   - SSR / 服务端渲染错误（本项目纯 CSR，不适用）
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // 开发期打到 console；生产期可以接入错误上报（Sentry 等）
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary]', error, info);
    }
    this.setState({ info });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.error) return this.props.children;

    const isDev = import.meta.env.DEV;

    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: 'linear-gradient(160deg, #F2F6F0 0%, #E8F0E4 100%)' }}
      >
        <div
          className="w-full max-w-lg rounded-2xl p-8"
          style={{ background: '#fff', boxShadow: '0 8px 32px rgba(0,0,0,0.08)', border: '1px solid rgba(180,195,175,0.3)' }}
        >
          <div className="flex items-center gap-3 mb-5">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(232,116,94,0.12)' }}
            >
              <i className="ri-error-warning-line text-2xl" style={{ color: '#E8745E' }}></i>
            </div>
            <div>
              <h1
                className="text-lg font-semibold"
                style={{ color: '#2C3E2D', fontFamily: '"Noto Serif SC", serif' }}
              >
                页面出了点问题
              </h1>
              <p className="text-xs mt-0.5" style={{ color: '#8AAA8A' }}>
                你可以刷新重试，或回到首页
              </p>
            </div>
          </div>

          {isDev && (
            <div
              className="rounded-xl p-3 mb-5 text-xs font-mono overflow-auto"
              style={{
                background: 'rgba(232,116,94,0.05)',
                border: '1px solid rgba(232,116,94,0.2)',
                color: '#8A4A3A',
                maxHeight: '220px',
              }}
            >
              <p className="font-semibold mb-1">{this.state.error.name}: {this.state.error.message}</p>
              {this.state.error.stack && (
                <pre className="whitespace-pre-wrap text-[11px] leading-relaxed" style={{ color: '#AA6A5A' }}>
                  {this.state.error.stack.split('\n').slice(0, 8).join('\n')}
                </pre>
              )}
              {this.state.info?.componentStack && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-[11px]" style={{ color: '#8A6A5A' }}>
                    Component stack
                  </summary>
                  <pre className="whitespace-pre-wrap text-[11px] leading-relaxed mt-1" style={{ color: '#AA6A5A' }}>
                    {this.state.info.componentStack}
                  </pre>
                </details>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={this.handleHome}
              className="flex-1 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap"
              style={{ background: 'rgba(180,195,175,0.15)', color: '#5C7A5C' }}
            >
              <i className="ri-home-4-line mr-1.5"></i>回到首页
            </button>
            <button
              onClick={this.handleReload}
              className="flex-1 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap"
              style={{ background: 'linear-gradient(135deg, #6B9E6B, #5A8A5A)', color: '#fff' }}
            >
              <i className="ri-refresh-line mr-1.5"></i>刷新重试
            </button>
          </div>
        </div>
      </div>
    );
  }
}
